import { t } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import type { ReactNode } from 'react';
import type { ImportDecision, PlanCandidate, PlanEntry } from '../../lib/types';

interface PlanDecisionProps {
  entry: PlanEntry;
  label: string;
  decision: ImportDecision | null;
  disabled: boolean;
  onDecide: (entryId: string, decision: ImportDecision) => void;
}

/** Called at render time of a component that uses `useLingui()`. */
function candidateLabel(candidate: PlanCandidate): string {
  const version = candidate.version ? ` ${candidate.version}` : '';
  const vendor = candidate.vendor ? ` · ${candidate.vendor}` : '';
  const target = `${candidate.name}${version}${vendor}`;
  return t`Zuordnen: ${target}`;
}

/**
 * Several catalog entries fit the scanned component. There is deliberately no
 * default: the server refuses the apply until every such entry is decided.
 */
function CandidateDecision({ entry, label, decision, disabled, onDecide }: PlanDecisionProps): ReactNode {
  const { t } = useLingui();
  const candidates = entry.display?.candidates ?? [];
  return (
    <select
      className="imp-decision imp-candidate-select"
      aria-label={t`Zuordnung für ${label}`}
      value={decision ?? ''}
      disabled={disabled}
      required
      onChange={(event) => {
        if (event.target.value !== '') onDecide(entry.entry_id, event.target.value as ImportDecision);
      }}
    >
      <option value="" disabled>{t`Bitte entscheiden …`}</option>
      {candidates.map((candidate) => (
        <option key={candidate.key} value={`match:${candidate.key}`}>
          {candidateLabel(candidate)}
        </option>
      ))}
      <option value="incoming">{t`Als neue Komponente anlegen`}</option>
    </select>
  );
}

/** Keeps every review choice tied to its original plan entry. */
export function PlanDecision(props: PlanDecisionProps): ReactNode {
  const { entry, label, decision, disabled, onDecide } = props;
  const { t } = useLingui();
  if (entry.action === 'unchanged') return null;
  if ((entry.display?.candidates?.length ?? 0) > 0) return <CandidateDecision {...props} />;
  const creating = entry.action === 'create';
  const removing = entry.action === 'remove';

  return (
    <div className="seg imp-decision" role="group" aria-label={t`Entscheidung für ${label}`}>
      <button
        type="button"
        className={decision === 'incoming' ? 'on' : undefined}
        aria-pressed={decision === 'incoming'}
        title={
          removing
            ? t`Aus der Stückliste entfernen`
            : creating ? t`Neu anlegen` : t`Dateiwerte übernehmen`
        }
        disabled={disabled}
        onClick={() => onDecide(entry.entry_id, 'incoming')}
      >
        {removing ? <Trans>Entfernen</Trans> : <Trans>Übernehmen</Trans>}
      </button>
      <button
        type="button"
        className={decision === 'existing' ? 'on keep' : undefined}
        aria-pressed={decision === 'existing'}
        title={
          removing
            ? t`In der Stückliste behalten (der Scan hat die Komponente evtl. nicht erkannt)`
            : creating ? t`Diesen neuen Eintrag überspringen` : t`Bisherigen Stand behalten`
        }
        disabled={disabled}
        onClick={() => onDecide(entry.entry_id, 'existing')}
      >
        {creating ? <Trans>Überspringen</Trans> : <Trans>Behalten</Trans>}
      </button>
    </div>
  );
}
